import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent as ReactDragEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { seedPricing } from '@rpl/pricing';
import { Chrome } from '../chrome/Chrome';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { track } from '../../lib/analytics';
import { CONTACT, DELIVERY_PROMISE } from '../../lib/contact';
import type { LocalPhoto, SortMode } from '../../lib/types';
import {
  batchProgress,
  etaSeconds,
  formatEta,
  isUploadInFlight,
  pushSample,
  rollingRate,
  type EtaSample,
} from '../../lib/uploadEta';
import { gbCapLabel, orderBytesLabel, planFileAcceptance, splitDuplicateFiles } from '../../lib/uploadLimits';
import { useCartExtras } from '../../store/cartExtras';
import { useStore } from '../../store/store';
import { ModalShell } from '../sizepaper/ModalShell';
import { Dropzone } from '../upload/Dropzone';
import {
  boxFromPoints,
  marqueeSelection,
  passedThreshold,
  rangeBetween,
  type Box,
  type MarqueeItem,
} from '../upload/marquee';
import { MobileCard } from '../upload/MobileCard';
import { OrderSidebar } from '../upload/OrderSidebar';
import { PhotoCard } from '../upload/PhotoCard';
import { SeoCardSlot } from '../upload/SeoCardSlot';
import { savePromptState } from '../../lib/savePrompt';
import { UNREADABLE_MSG } from '../../lib/decode';
import '../upload/upload.css';

function sortPhotos(photos: LocalPhoto[], sort: string): LocalPhoto[] {
  const arr = photos.slice();
  if (sort === 'name') arr.sort((a, b) => a.filename.localeCompare(b.filename));
  else if (sort === 'mp-desc') arr.sort((a, b) => b.mp - a.mp);
  else arr.sort((a, b) => a.sortOrder - b.sortOrder);
  return arr;
}

export function UploadScreen() {
  const isMobile = useIsMobile();
  const photos = useStore((s) => s.photos);
  const pricing = useStore((s) => s.pricing);
  const sort = useStore((s) => s.sort);
  const selection = useStore((s) => s.selection);
  const addFiles = useStore((s) => s.addFiles);
  const removePhoto = useStore((s) => s.removePhoto);
  const retryPhoto = useStore((s) => s.retryPhoto);
  const setSort = useStore((s) => s.setSort);
  const setStep = useStore((s) => s.setStep);
  const selectAll = useStore((s) => s.selectAll);
  const clearSelection = useStore((s) => s.clearSelection);
  const setSelection = useStore((s) => s.setSelection);
  const toggleSelect = useStore((s) => s.toggleSelect);

  const limits = pricing?.limits ?? seedPricing.limits;
  const maxPhotos = limits.maxPhotosPerOrder;
  const totalBytes = useMemo(() => photos.reduce((n, p) => n + p.bytes, 0), [photos]);
  const capLabel = gbCapLabel(limits.maxOrderBytes);
  // "Full" = the 5 GB cap (or the distant photo-count safety rail) is reached.
  const atLimit = photos.length >= maxPhotos || totalBytes >= limits.maxOrderBytes;

  const sorted = useMemo(() => sortPhotos(photos, sort), [photos, sort]);

  // Clarity: users on step 0 click the stepper's "Upload" label expecting the
  // file picker — the mounted dropzone registers its picker here.
  const pickRef = useRef<(() => void) | null>(null);

  const readyPhotos = photos.filter((p) => p.status === 'ready');
  const hasReady = readyPhotos.length > 0;
  const readyIds = useMemo(() => sorted.filter((p) => p.status === 'ready').map((p) => p.id), [sorted]);
  const selectedCount = useMemo(
    () => readyPhotos.reduce((n, p) => n + (selection.has(p.id) ? 1 : 0), 0),
    [readyPhotos, selection],
  );
  const allSelected = hasReady && selectedCount === readyPhotos.length;
  const inFlightCount = useMemo(
    () => photos.reduce((n, p) => n + (isUploadInFlight(p.status) ? 1 : 0), 0),
    [photos],
  );
  const uploadsActive = inFlightCount > 0;

  /* ---------------- aggregate upload progress + ETA strip (round 4) ---------------- */
  const [strip, setStrip] = useState<{
    percent: number;
    label: string;
    count: number;
    /** Owner 2026-07-27: the strip HOLDS on a "done" state instead of
     *  vanishing — the sudden unmount shifted the page and left no
     *  completion signal. */
    done?: boolean;
  } | null>(null);
  const stripActiveRef = useRef(false);
  const doneTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const baselineRef = useRef(0); // Σbytes already 'ready' when this batch began
  const samplesRef = useRef<EtaSample[]>([]);

  useEffect(() => {
    if (!uploadsActive) {
      samplesRef.current = [];
      if (stripActiveRef.current) {
        // Batch just finished: hold a completed strip briefly (no layout jump,
        // clear "done" signal), then retire it.
        stripActiveRef.current = false;
        const uploaded = useStore
          .getState()
          .photos.reduce((n, p) => n + (p.status === 'ready' ? 1 : 0), 0);
        setStrip({ percent: 100, label: '', count: uploaded, done: true });
        doneTimerRef.current = setTimeout(() => setStrip(null), 6000);
      }
      return;
    }
    if (doneTimerRef.current) {
      clearTimeout(doneTimerRef.current); // a new batch started mid-hold
      doneTimerRef.current = null;
    }
    if (!stripActiveRef.current) {
      stripActiveRef.current = true;
      baselineRef.current = useStore
        .getState()
        .photos.reduce((n, p) => n + (p.status === 'ready' ? p.bytes : 0), 0);
      samplesRef.current = [];
    }
    // Cheap 1s cadence: sample the store's existing progress state, no extra I/O.
    const tick = () => {
      const snapshot = useStore.getState().photos;
      const agg = batchProgress(snapshot, baselineRef.current);
      const now = Date.now();
      samplesRef.current = pushSample(samplesRef.current, now, agg.doneBytes);
      const eta = etaSeconds(agg.totalBytes - agg.doneBytes, rollingRate(samplesRef.current));
      const count = snapshot.reduce((n, p) => n + (isUploadInFlight(p.status) ? 1 : 0), 0);
      setStrip({
        percent: agg.percent,
        label: eta == null ? 'estimating…' : formatEta(eta),
        count,
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [uploadsActive]);

  /* ---------------- batch-complete confirmation dialog (round 4) ----------------
     Round 7: failures are FLAGGED, loudly. The dialog fires whenever a batch
     settles with new ready photos OR any failed ones (including all-failed
     batches, which previously showed nothing at all) — a customer must never
     continue believing every photo made it when some didn't. */
  const [batchDone, setBatchDone] = useState<{ ready: number; failed: LocalPhoto[] } | null>(null);
  // Post-upload save prompt (owner 2026-07-29). savePromptSeen/savedEmail are
  // store-held so this screen unmounting on the way to Step 2 does not re-arm it.
  const savedEmail = useCartExtras((s) => s.savedEmail);
  const savePromptSeen = useCartExtras((s) => s.savePromptSeen);
  const markSavePromptSeen = useCartExtras((s) => s.markSavePromptSeen);
  const saveOrder = useCartExtras((s) => s.saveOrder);
  const [saveEmail, setSaveEmail] = useState('');
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [savingPrompt, setSavingPrompt] = useState(false);
  const prevInFlightRef = useRef(0);
  const readyAtDialogRef = useRef(-1);
  const readyAtBatchStartRef = useRef(0);
  useEffect(() => {
    const prev = prevInFlightRef.current;
    prevInFlightRef.current = inFlightCount;
    const readyNow = photos.reduce((n, p) => n + (p.status === 'ready' ? 1 : 0), 0);
    if (prev === 0 && inFlightCount > 0) {
      // Batch starting — remember the ready count so we know the batch size.
      readyAtBatchStartRef.current = readyNow;
    }
    if (prev > 0 && inFlightCount === 0) {
      const failed = photos.filter((p) => p.status === 'error');
      if ((readyNow > 0 && readyNow !== readyAtDialogRef.current) || failed.length > 0) {
        readyAtDialogRef.current = readyNow;
        track('upload_batch_done', { ready: readyNow, failed: failed.length });
        // A clean single-photo batch skips the dialog — people adding one photo
        // at a time found the confirmation nagging. Failures always show.
        const newlyReady = readyNow - readyAtBatchStartRef.current;
        if (failed.length === 0 && newlyReady <= 1) return;
        setBatchDone({ ready: readyNow, failed });
      }
    }
  }, [inFlightCount, photos]);

  /* ---------------- multi-select delete (round 4, mirrors Step 2) ---------------- */
  const [confirmDelete, setConfirmDelete] = useState<string[] | null>(null);
  // Round 7: mobile gets an explicit select mode (no marquee/shift-click there).
  const [mobileSelect, setMobileSelect] = useState(false);
  const exitMobileSelect = () => {
    setMobileSelect(false);
    clearSelection();
  };
  const doBulkDelete = () => {
    if (!confirmDelete) return;
    for (const id of confirmDelete) removePhoto(id);
    setConfirmDelete(null);
    if (mobileSelect) exitMobileSelect();
  };

  /* ---------------- order-size limits (5 GB cap + photo safety rail) ---------------- */
  const [limitError, setLimitError] = useState<string | null>(null);
  const limitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (limitTimer.current) clearTimeout(limitTimer.current); }, []);

  const flashLimit = useCallback((message: string) => {
    setLimitError(message);
    if (limitTimer.current) clearTimeout(limitTimer.current);
    limitTimer.current = setTimeout(() => setLimitError(null), 6000);
  }, []);

  const handleFiles = useCallback(
    (offered: File[]) => {
      // Duplicates first (same filename + size as a photo already here): the
      // store runs the same split and skips them — one photo can still go on
      // several papers in Size & Paper, so a second copy only clutters.
      const { fresh: files, duplicates } = splitDuplicateFiles(photos, offered);
      if (duplicates.length > 0) {
        const names = duplicates.slice(0, 3).map((f) => f.name).join(', ');
        const more = duplicates.length > 3 ? ` +${duplicates.length - 3} more` : '';
        flashLimit(
          `Already in your order — skipped: ${names}${more}. ` +
            `To print the same photo on different sizes or papers, set that up in the next step.`,
        );
        if (files.length === 0) return;
      }
      // Plan against the caps so the messaging matches exactly what the store
      // will accept (the store runs the same plan and skips the overflow).
      const plan = planFileAcceptance(
        { count: photos.length, bytes: totalBytes },
        files.map((f) => f.size),
        limits,
      );
      if (plan.rejectedBytes > 0) {
        flashLimit(
          `That would take this order past the ${capLabel} upload limit. ` +
            `You're at ${orderBytesLabel(totalBytes, limits.maxOrderBytes)}. Start a second order for the rest.`,
        );
      } else if (plan.rejectedCount > 0) {
        flashLimit(`That's the ${maxPhotos}-photo limit for one order. Start a second order for more.`);
      }
      if (plan.acceptedIndexes.length > 0) void addFiles(files);
    },
    [photos, totalBytes, limits, capLabel, maxPhotos, addFiles, flashLimit],
  );

  /* ---------------- selection: shift-click range ---------------- */
  const anchorRef = useRef<string | null>(null);
  const draggedRef = useRef(false);

  const handleToggle = useCallback(
    (id: string, shiftKey: boolean) => {
      if (draggedRef.current) return; // trailing click of a marquee drag
      if (shiftKey && anchorRef.current) {
        const ids = rangeBetween(readyIds, anchorRef.current, id);
        if (ids.length) {
          const next = new Set(selection);
          for (const x of ids) next.add(x);
          setSelection(next);
          return;
        }
      }
      toggleSelect(id);
      anchorRef.current = id;
    },
    [readyIds, selection, setSelection, toggleSelect],
  );

  /* ---------------- selection: rubber-band marquee ---------------- */
  const gridRef = useRef<HTMLDivElement>(null);
  const startRef = useRef<{ x: number; y: number; base: Set<string> } | null>(null);
  const [marquee, setMarquee] = useState<Box | null>(null);
  const [gridOver, setGridOver] = useState(false);

  const collectItems = useCallback((): MarqueeItem[] => {
    const grid = gridRef.current;
    if (!grid) return [];
    const items: MarqueeItem[] = [];
    grid.querySelectorAll<HTMLElement>('[data-photo-id]').forEach((n) => {
      const id = n.dataset.photoId;
      if (!id) return;
      const r = n.getBoundingClientRect();
      items.push({ id, box: { left: r.left, top: r.top, right: r.right, bottom: r.bottom } });
    });
    return items;
  }, []);

  function onPointerDown(e: ReactPointerEvent) {
    if (e.button !== 0 || e.pointerType === 'touch') return;
    const target = e.target as HTMLElement;
    if (target.closest('button, a, input, select, label')) return; // real controls behave normally
    startRef.current = { x: e.clientX, y: e.clientY, base: e.shiftKey ? new Set(selection) : new Set() };
    draggedRef.current = false;
  }
  function onPointerMove(e: ReactPointerEvent) {
    const s = startRef.current;
    if (!s) return;
    if (!draggedRef.current && !passedThreshold(s.x, s.y, e.clientX, e.clientY)) return;
    if (!draggedRef.current) {
      draggedRef.current = true;
      try {
        gridRef.current?.setPointerCapture(e.pointerId);
      } catch {
        /* capture unsupported — marquee still works */
      }
    }
    const box = boxFromPoints(s.x, s.y, e.clientX, e.clientY);
    setMarquee(box);
    setSelection(marqueeSelection(s.base, box, collectItems()));
  }
  function onPointerUp(e: ReactPointerEvent) {
    if (!startRef.current) return;
    startRef.current = null;
    setMarquee(null);
    if (gridRef.current?.hasPointerCapture?.(e.pointerId)) {
      try {
        gridRef.current.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    }
    if (draggedRef.current) {
      // keep the guard up through the click that immediately follows, then reset
      setTimeout(() => {
        draggedRef.current = false;
      }, 0);
    }
  }

  // Marquee overlay geometry (viewport → grid-relative).
  const marqueeStyle = useMemo(() => {
    if (!marquee || !gridRef.current) return null;
    const wrap = gridRef.current.getBoundingClientRect();
    return {
      left: marquee.left - wrap.left,
      top: marquee.top - wrap.top,
      width: marquee.right - marquee.left,
      height: marquee.bottom - marquee.top,
    };
  }, [marquee]);

  /* ---------------- drag files onto the grid ---------------- */
  function onGridDragOver(e: ReactDragEvent) {
    e.preventDefault();
    if (!atLimit) setGridOver(true);
  }
  function onGridDragLeave() {
    setGridOver(false);
  }
  function onGridDrop(e: ReactDragEvent) {
    e.preventDefault();
    setGridOver(false);
    const files = e.dataTransfer?.files ? Array.from(e.dataTransfer.files) : [];
    if (files.length) handleFiles(files);
  }

  const continueToSizes = () => {
    // spTab is store-held now — a stale 'configure' from an earlier visit
    // showed a BLANK Customise tab on arrival. Fresh entry = Photos first.
    useStore.getState().setSpTab('photos');
    setStep(1);
  };

  /* Ask for the email on the FIRST successful batch only, and never once the
     order is already saved. This modal fires per batch, so without the guard a
     three-batch upload asks three times. */
  const { ask: askSave, wantsSave, invalid: saveInvalid } = savePromptState({
    seen: savePromptSeen,
    savedEmail,
    typed: saveEmail,
  });

  /** The modal's primary button: save first when an address was typed, then go. */
  async function continueFromBatch() {
    const value = saveEmail.trim();
    if (saveInvalid) {
      // Typed something that is not an address: correct it rather than binning
      // it silently, since they clearly meant to save.
      setSaveErr("That doesn't look like an email address. Check it, or clear it to skip.");
      return;
    }
    if (wantsSave) {
      setSavingPrompt(true);
      try {
        await saveOrder(value, 'upload_prompt');
      } catch {
        // A failed save must never trap them on the upload step. The order is
        // still perfectly usable unsaved, and the Step 2 link offers it again.
      } finally {
        setSavingPrompt(false);
      }
    }
    markSavePromptSeen();
    setBatchDone(null);
    continueToSizes();
  }

  const failedCount = photos.reduce((n, p) => n + (p.status === 'error' ? 1 : 0), 0);
  const footerStatus =
    photos.length === 0
      ? 'Add at least one photo to continue'
      : uploadsActive
        ? `Uploading ${inFlightCount} photo${inFlightCount === 1 ? '' : 's'}… ${
            strip ? `${strip.percent}% · ${strip.label}` : ''
          }`
        : `${photos.length} photo${photos.length === 1 ? '' : 's'}${
            failedCount > 0 ? ` · ${failedCount} FAILED` : ''
          }${
            selectedCount > 0 ? ` · ${selectedCount} selected` : ''
          } · ${orderBytesLabel(totalBytes, limits.maxOrderBytes)}`;

  /* Aggregate progress strip — overall % + rolling-average ETA. On finish it
     HOLDS a completed state for a few seconds (owner 2026-07-27) instead of
     vanishing mid-layout. */
  const etaStrip = strip && (
    <div className={`upstrip${strip.done ? ' upstrip--done' : ''}`} role="status" aria-live="polite">
      <div className="upstrip__row">
        <span className="upstrip__text">
          {strip.done
            ? `✓ ${strip.count} photo${strip.count === 1 ? '' : 's'} uploaded`
            : `Uploading ${strip.count} photo${strip.count === 1 ? '' : 's'} · ${strip.percent}%`}
        </span>
        <span className="upstrip__eta">{strip.done ? 'All done' : strip.label}</span>
      </div>
      <div className="upstrip__bar" aria-hidden="true">
        <div className="upstrip__fill" style={{ width: `${strip.percent}%` }} />
      </div>
    </div>
  );

  const limitBanner = limitError && (
    <div className="uploadlimit" role="status">
      <span className="uploadlimit__icon" aria-hidden="true">
        !
      </span>
      <span className="uploadlimit__msg">{limitError}</span>
      <button
        type="button"
        className="uploadlimit__x"
        onClick={() => setLimitError(null)}
        aria-label="Dismiss"
      >
        ✕
      </button>
    </div>
  );

  const desktopBody = (
    <div className="upload__body">
      <div className="upload__main">
        {photos.length === 0 ? (
          <>
            <Dropzone onFiles={handleFiles} capLabel={capLabel} full={atLimit} pickRef={pickRef} />
            {limitBanner}
            <SeoCardSlot />
          </>
        ) : (
          <>
            {etaStrip}
            <div className="upload__toolbar">
              <div className="upload__count">
                {photos.length} photo{photos.length === 1 ? '' : 's'} uploaded
                <span className="upload__bytes"> · {orderBytesLabel(totalBytes, limits.maxOrderBytes)}</span>
                {selectedCount > 0 && (
                  <span className="upload__selcount"> · {selectedCount} selected</span>
                )}
              </div>
              <div className="upload__tools">
                <button
                  type="button"
                  className="linkbtn"
                  onClick={allSelected ? clearSelection : selectAll}
                  disabled={!hasReady}
                >
                  {allSelected ? 'Clear selection' : `Select all${hasReady ? ` (${readyPhotos.length})` : ''}`}
                </button>
                {selectedCount > 0 && (
                  <button
                    type="button"
                    className="linkbtn upload__delete"
                    onClick={() =>
                      setConfirmDelete(readyPhotos.filter((p) => selection.has(p.id)).map((p) => p.id))
                    }
                  >
                    Delete ({selectedCount})
                  </button>
                )}
                <label className="upload__sort">
                  <span>Sort</span>
                  <select
                    className="upload__sortsel"
                    value={sort}
                    onChange={(e) => setSort(e.target.value as SortMode)}
                    aria-label="Sort photos"
                  >
                    <option value="upload">Upload order</option>
                    <option value="name">Filename A–Z</option>
                    <option value="mp-desc">Largest first</option>
                  </select>
                </label>
              </div>
            </div>

            {limitBanner}

            <Dropzone variant="compact" onFiles={handleFiles} capLabel={capLabel} full={atLimit} pickRef={pickRef} />

            <div className="photogrid-scroll">
              <div
                className={`photogrid-wrap${gridOver ? ' photogrid-wrap--dropping' : ''}`}
                ref={gridRef}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onDragOver={onGridDragOver}
                onDragLeave={onGridDragLeave}
                onDrop={onGridDrop}
              >
                <div className="photogrid">
                  {sorted.map((p) => (
                    <PhotoCard
                      key={p.id}
                      photo={p}
                      selected={selection.has(p.id)}
                      selectable={p.status === 'ready'}
                      onToggle={(shift) => handleToggle(p.id, shift)}
                      onRemove={() => removePhoto(p.id)}
                      onRetry={() => retryPhoto(p.id)}
                    />
                  ))}
                </div>
                {marqueeStyle && <div className="marquee" style={marqueeStyle} aria-hidden="true" />}
              </div>
            </div>
          </>
        )}
      </div>

      <OrderSidebar />
    </div>
  );

  const mobileBody = (
    <div className="mupload">
      <h2 className="mupload__title">Upload photos</h2>
      <p className="mupload__promise">{DELIVERY_PROMISE}</p>
      {etaStrip}
      <Dropzone variant="mobile" onFiles={handleFiles} capLabel={capLabel} full={atLimit} pickRef={pickRef} />
      {limitBanner}
      {photos.length === 0 && <SeoCardSlot />}
      {photos.length > 0 && (
        <div className="mupload__count">
          <span>
            {photos.length} photo{photos.length === 1 ? '' : 's'} ·{' '}
            {orderBytesLabel(totalBytes, limits.maxOrderBytes)}
            {mobileSelect && selectedCount > 0 && ` · ${selectedCount} selected`}
          </span>
          {hasReady &&
            (mobileSelect ? (
              <span className="mupload__tools">
                <button
                  type="button"
                  className="linkbtn"
                  onClick={allSelected ? clearSelection : selectAll}
                >
                  {allSelected ? 'Clear' : 'All'}
                </button>
                <button
                  type="button"
                  className="linkbtn upload__delete"
                  disabled={selectedCount === 0}
                  onClick={() =>
                    setConfirmDelete(readyPhotos.filter((p) => selection.has(p.id)).map((p) => p.id))
                  }
                >
                  Delete{selectedCount > 0 ? ` (${selectedCount})` : ''}
                </button>
                <button type="button" className="linkbtn" onClick={exitMobileSelect}>
                  Done
                </button>
              </span>
            ) : (
              <button type="button" className="linkbtn" onClick={() => setMobileSelect(true)}>
                Select
              </button>
            ))}
        </div>
      )}
      <div className="photogrid photogrid--mobile">
        {sorted.map((p) => (
          <MobileCard
            key={p.id}
            photo={p}
            onRemove={() => removePhoto(p.id)}
            selectMode={mobileSelect}
            selected={selection.has(p.id)}
            onToggle={() => p.status === 'ready' && toggleSelect(p.id)}
          />
        ))}
      </div>
    </div>
  );

  return (
    <Chrome
      step={0}
      onActiveStepClick={() => pickRef.current?.()}
      activeStepTitle="Add photos"
      headerRight={
        <span>
          Need help?{' '}
          <a href={`tel:${CONTACT.phone.replace(/[^\d+]/g, '')}`}>{CONTACT.phone}</a>
        </span>
      }
      footer={{
        status: footerStatus,
        primary: {
          label: 'Continue to Size & Paper →',
          onClick: continueToSizes,
          // Round 4: Continue waits for EVERY upload to finish (no in-flight).
          disabled: !hasReady || uploadsActive,
        },
      }}
      mobileFooter={{
        primary: { label: 'Continue →', onClick: continueToSizes, disabled: !hasReady || uploadsActive },
      }}
    >
      {isMobile ? mobileBody : desktopBody}

      {batchDone != null && (
        <ModalShell onClose={() => setBatchDone(null)} width={460} label="Photos uploaded" className="confirm">
          <div className="confirm__body">
            <h3 className="confirm__title">
              {batchDone.failed.length > 0
                ? `${batchDone.ready} uploaded — ${batchDone.failed.length} FAILED`
                : `${batchDone.ready} photo${batchDone.ready === 1 ? '' : 's'} uploaded — step 1 of 4 done`}
            </h3>
            {batchDone.failed.length > 0 ? (
              <>
                <p className="confirm__text confirm__text--danger">
                  These photos are NOT in your order:
                </p>
                {/* Owner 2026-10-20: 597 Google Photos picks on one Android phone
                    all failed NotReadableError, and the customer left with
                    nothing. Say what to do once, above the list. */}
                {batchDone.failed.some((p) => p.error === UNREADABLE_MSG) && (
                  <p className="confirm__text">
                    Your phone couldn&apos;t open these photos. If they&apos;re in Google Photos,
                    download them to your phone first, or choose them from your Gallery or
                    Files app. Picking about 100 at a time also helps.
                  </p>
                )}
                <ul className="confirm__fails">
                  {batchDone.failed.slice(0, 4).map((p) => (
                    <li key={p.id}>
                      <strong>{p.filename}</strong>
                      {p.error ? ` — ${p.error}` : ''}
                    </li>
                  ))}
                  {batchDone.failed.length > 4 && (
                    <li>…and {batchDone.failed.length - 4} more (marked red in the grid)</li>
                  )}
                </ul>
                <div className="confirm__actions">
                  <button
                    type="button"
                    className="btn btn--secondary"
                    onClick={() => {
                      const ids = batchDone.failed.map((p) => p.id);
                      setBatchDone(null);
                      for (const id of ids) removePhoto(id);
                    }}
                  >
                    Remove failed
                  </button>
                  <button
                    type="button"
                    className="btn btn--primary"
                    onClick={() => {
                      const ids = batchDone.failed.map((p) => p.id);
                      setBatchDone(null);
                      for (const id of ids) retryPhoto(id);
                    }}
                  >
                    Retry failed
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Email capture rides the modal they already have to dismiss
                    (owner 2026-07-29). 408 abandoned drafts held ONE email
                    between them, because the only save prompt lived on the cart
                    screen and just 36 of those drafts ever reached it. 116 of
                    them uploaded, so this is the widest point in the funnel
                    where there is anything worth saving.

                    Optional by construction: the field is ignorable and the
                    primary button is byte-identical to before until an address
                    is actually typed, so nobody is gated and there is no "skip"
                    to decide about. */}
                {askSave ? (
                  <>
                    <p className="confirm__text">
                      Coming back to this later? Email yourself a link and we'll keep your
                      photos loaded for 30 days.
                    </p>
                    {/* "Optional" said THREE ways, because this must never read as
                        a form to complete: a visible label that survives typing
                        (the placeholder does not), the placeholder itself, and the
                        hint under the field. Owner: always keep the ask
                        low-commitment. */}
                    <label className="confirm__label" htmlFor="rpl-save-email">
                      Your email <span className="confirm__optional">optional</span>
                    </label>
                    <input
                      id="rpl-save-email"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      className="confirm__input"
                      placeholder="you@example.com"
                      aria-describedby="rpl-save-hint"
                      value={saveEmail}
                      onChange={(e) => {
                        setSaveEmail(e.target.value);
                        if (saveErr) setSaveErr(null);
                      }}
                      /* No autoFocus: on a phone it throws up the keyboard and
                         covers the very buttons this modal exists to offer. */
                    />
                    <p className="confirm__hint" id="rpl-save-hint">
                      Leave it blank to carry on without saving.
                    </p>
                    {saveErr && <p className="confirm__text confirm__text--danger">{saveErr}</p>}
                  </>
                ) : (
                  <p className="confirm__text">
                    Add more photos, or continue to pick sizes &amp; papers.
                  </p>
                )}
                <div className="confirm__actions">
                  <button type="button" className="btn btn--secondary" onClick={() => setBatchDone(null)}>
                    Add more
                  </button>
                  <button
                    type="button"
                    className="btn btn--primary"
                    disabled={savingPrompt}
                    onClick={() => void continueFromBatch()}
                  >
                    {savingPrompt
                      ? 'Saving…'
                      : wantsSave
                        ? 'Save & continue →'
                        : 'Continue →'}
                  </button>
                </div>
              </>
            )}
          </div>
        </ModalShell>
      )}

      {confirmDelete && (
        <ModalShell onClose={() => setConfirmDelete(null)} width={420} label="Delete photos" className="confirm">
          <div className="confirm__body">
            <h3 className="confirm__title">
              Delete {confirmDelete.length} photo{confirmDelete.length === 1 ? '' : 's'}?
            </h3>
            <p className="confirm__text">You&rsquo;ll need to re-upload them.</p>
            <div className="confirm__actions">
              <button type="button" className="btn btn--secondary" onClick={() => setConfirmDelete(null)}>
                Keep them
              </button>
              <button type="button" className="btn btn--primary confirm__danger" onClick={doBulkDelete}>
                Delete {confirmDelete.length}
              </button>
            </div>
          </div>
        </ModalShell>
      )}
    </Chrome>
  );
}
